import React, { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import {
  applyBrandTheme,
  buildThemeFromConfig,
  readCachedBranding,
  writeCachedBranding,
  preloadBrandAssetUrls,
} from './lib/utils';

const LandingPage = lazy(() => import('./pages/LandingPage'));
const MobileChatPage = lazy(() => import('./pages/MobileChatPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminLeads = lazy(() => import('./pages/admin/AdminLeads'));
const AdminLeadDetail = lazy(() => import('./pages/admin/AdminLeadDetail'));
const AdminAppointments = lazy(() => import('./pages/admin/AdminAppointments'));
const AdminDentists = lazy(() => import('./pages/admin/AdminDentists'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));
const AdminAlerts = lazy(() => import('./pages/admin/AdminAlerts'));
const AdminServices = lazy(() => import('./pages/admin/AdminServices'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminMyProfile = lazy(() => import('./pages/admin/AdminMyProfile'));

const ROLE = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
  RECEPTION: 'reception',
  DENTIST: 'dentist',
  STAFF: 'staff',
};

const normalizeRole = (role) => {
  if (!role) return null;
  return role === ROLE.STAFF ? ROLE.RECEPTION : role;
};

const readStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}');
  } catch {
    return {};
  }
};

const Loader = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50">
    <div className="flex flex-col items-center gap-4">
      <div
        className="w-10 h-10 rounded-full border-4 border-t-transparent animate-spin"
        style={{
          borderColor: 'var(--brand-primary)',
          borderTopColor: 'transparent',
        }}
      />
      <p className="text-slate-600">Cargando...</p>
    </div>
  </div>
);

const ProtectedRoute = ({ children, allowedRoles = null }) => {
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  const [token, setToken] = useState(null);
  const [user, setUser] = useState({});

  useEffect(() => {
    const nextToken = localStorage.getItem('auth_token');
    const nextUser = readStoredUser();
    setToken(nextToken);
    setUser(nextUser);
    setIsChecking(false);
  }, [location.pathname]);

  const effectiveRole = useMemo(() => normalizeRole(user?.role), [user]);

  if (isChecking) return <Loader />;
  if (!token) return <Navigate to="/admin/login" replace state={{ from: location }} />;

  if (user?.is_active === false) {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    return <Navigate to="/admin/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(effectiveRole)) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  return children;
};

function App() {
  useEffect(() => {
    const cached = readCachedBranding();

    if (cached) {
      applyBrandTheme(cached);
      preloadBrandAssetUrls(cached);
    } else {
      applyBrandTheme(buildThemeFromConfig({}));
    }

    let active = true;

    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/public/site-config`)
      .then((response) => {
        if (!active) return;
        const data = response?.data || {};
        applyBrandTheme(data);
        writeCachedBranding(data);
        preloadBrandAssetUrls(data);
      })
      .catch((error) => {
        console.error('Error loading public site config for theme bootstrap:', error);
      });

    return () => {
      active = false;
    };
  }, []);

  return (
    <>
      <Toaster position="top-right" richColors closeButton />
      <BrowserRouter>
        <Suspense fallback={<Loader />}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/chat" element={<MobileChatPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/admin/login" element={<AdminLogin />} />

            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute
                  allowedRoles={[
                    ROLE.SUPER_ADMIN,
                    ROLE.ADMIN,
                    ROLE.RECEPTION,
                    ROLE.DENTIST,
                  ]}
                >
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/mi-perfil"
              element={
                <ProtectedRoute allowedRoles={[ROLE.DENTIST]}>
                  <AdminMyProfile />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/leads"
              element={
                <ProtectedRoute
                  allowedRoles={[
                    ROLE.SUPER_ADMIN,
                    ROLE.ADMIN,
                    ROLE.RECEPTION,
                    ROLE.DENTIST,
                  ]}
                >
                  <AdminLeads />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/leads/:id"
              element={
                <ProtectedRoute
                  allowedRoles={[
                    ROLE.SUPER_ADMIN,
                    ROLE.ADMIN,
                    ROLE.RECEPTION,
                    ROLE.DENTIST,
                  ]}
                >
                  <AdminLeadDetail />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/citas"
              element={
                <ProtectedRoute
                  allowedRoles={[
                    ROLE.SUPER_ADMIN,
                    ROLE.ADMIN,
                    ROLE.RECEPTION,
                    ROLE.DENTIST,
                  ]}
                >
                  <AdminAppointments />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/dentistas"
              element={
                <ProtectedRoute
                  allowedRoles={[
                    ROLE.SUPER_ADMIN,
                    ROLE.ADMIN,
                    ROLE.RECEPTION,
                    ROLE.DENTIST,
                  ]}
                >
                  <AdminDentists />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/alertas"
              element={
                <ProtectedRoute
                  allowedRoles={[
                    ROLE.SUPER_ADMIN,
                    ROLE.ADMIN,
                    ROLE.RECEPTION,
                    ROLE.DENTIST,
                  ]}
                >
                  <AdminAlerts />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/servicios"
              element={
                <ProtectedRoute
                  allowedRoles={[
                    ROLE.SUPER_ADMIN,
                    ROLE.ADMIN,
                    ROLE.RECEPTION,
                    ROLE.DENTIST,
                  ]}
                >
                  <AdminServices />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/configuracion"
              element={
                <ProtectedRoute
                  allowedRoles={[
                    ROLE.SUPER_ADMIN,
                    ROLE.ADMIN,
                  ]}
                >
                  <AdminSettings />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/usuarios"
              element={
                <ProtectedRoute allowedRoles={[ROLE.SUPER_ADMIN]}>
                  <AdminUsers />
                </ProtectedRoute>
              }
            />

            <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </>
  );
}

export default App;